import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type DentalProcedureCodeRow = Database['public']['Tables']['dental_procedure_codes']['Row'];

// ── Query keys ────────────────────────────────────────────────────────────────

export const procedureCodesQueryKey = () => ['dental', 'procedure-codes'] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

export function useProcedureCodes() {
  return useQuery({
    queryKey: procedureCodesQueryKey(),
    queryFn: async (): Promise<DentalProcedureCodeRow[]> => {
      const { data, error } = await supabase
        .from('dental_procedure_codes')
        .select('*')
        .order('code', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useProcedureCodeByCode(code: string | null | undefined) {
  const { data: all, ...rest } = useProcedureCodes();
  const row = all?.find((r) => r.code === code) ?? null;
  return { data: row, ...rest };
}

export function useProcedureCodeSearch(query: string) {
  const { data: all, ...rest } = useProcedureCodes();
  const q = query.trim().toLowerCase();
  const results = q
    ? (all ?? []).filter(
        (r) =>
          r.code.startsWith(q) ||
          r.name_da.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q),
      )
    : (all ?? []);
  return { data: results, ...rest };
}
